import type { MessagePart } from '@elara/domain/models/schemas/message-schemas';

import type { ToolCall } from '#models/ToolCallModel.js';
import type { ThinkingPlaceholderRotationState } from './thinking-placeholders.js';

import { isReasoningPart } from '@elara/domain/models/schemas/message-schemas';

import { findLatestInProgressToolCall } from './message-parts.js';
import {
  createThinkingPlaceholderRotationState,
  pickNextThinkingPlaceholder,
  resolveThinkingPlaceholder,
} from './thinking-placeholders.js';
import { getToolCallInProgressLabel } from './tool-badges/registry.js';

/**
 * Resolves the display text for a "model is thinking" indicator — shared by web
 * (`MessageThinkingIndicator`) and mobile (`ThinkingIndicator`) so both platforms show the exact same
 * text for the exact same message state.
 *
 * Priority: an active tool call's own in-progress label (e.g. "Searching for outbound flights") wins
 * over a generic phrase, since it tells the traveler what's actually happening. Otherwise a rotating
 * placeholder phrase is shown, held stable across re-renders and only advancing when a NEW reasoning
 * block appears in the streamed content — so it doesn't flicker mid-thought, but still feels alive
 * across a multi-step reasoning response.
 */

export interface ThinkingIndicatorState {
  messageId?: string;
  reasoningCount: number;
  rotation: ThinkingPlaceholderRotationState;
}

export function createThinkingIndicatorState(): ThinkingIndicatorState {
  return { reasoningCount: 0, rotation: createThinkingPlaceholderRotationState() };
}

export interface ThinkingIndicatorInput {
  /** Stable identity for the message being displayed — resets the rotation when it changes. */
  messageId: string;
  content: MessagePart[];
  toolCalls: ToolCall[];
}

export function resolveThinkingIndicatorText(
  { messageId, content, toolCalls }: ThinkingIndicatorInput,
  state: ThinkingIndicatorState,
): string {
  const latestInProgress = findLatestInProgressToolCall(content, toolCalls);
  if (latestInProgress) {
    const inProgressLabel = getToolCallInProgressLabel(latestInProgress);
    if (inProgressLabel) return inProgressLabel;
    state.messageId = messageId;
    state.reasoningCount = content.reduce((count, part) => (isReasoningPart(part) ? count + 1 : count), 0);
    return resolveThinkingPlaceholder(state.rotation.currentIndex);
  }

  const reasoningCount = content.reduce((count, part) => (isReasoningPart(part) ? count + 1 : count), 0);
  const isNewMessage = state.messageId !== messageId;
  const sawNewReasoningBlock = reasoningCount > state.reasoningCount;
  const needsInitialPlaceholder = state.rotation.currentIndex < 0 || isNewMessage;

  if (needsInitialPlaceholder || sawNewReasoningBlock) {
    const nextPlaceholder = pickNextThinkingPlaceholder(state.rotation);
    state.messageId = messageId;
    state.reasoningCount = reasoningCount;
    return nextPlaceholder.text;
  }

  state.messageId = messageId;
  state.reasoningCount = reasoningCount;
  return resolveThinkingPlaceholder(state.rotation.currentIndex);
}
